// ONE reset page, rendered per audience via the `audience` prop, styled to match the login
// screens (dark overlay + glass card) instead of the bare Bootstrap card it replaces.
//
// The success message deliberately does NOT confirm the account exists — the backend returns
// the same generic reply for unknown, ineligible and ambiguous addresses so the endpoint cannot
// be used to enumerate accounts, and the UI must not undo that by saying "we found you".

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { FORGOT_PASSWORD_AUDIENCES } from "./forgotPasswordAudiences";

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const ForgotPassword = ({ audience = "student" }) => {
  const config = FORGOT_PASSWORD_AUDIENCES[audience];
  const [sent, setSent] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await config.submit({ email: data.email.trim() });
      // Terminal state rather than a redirect: the old page navigated to "/" immediately, so
      // the one instruction that matters ("check your inbox, including spam") vanished with it.
      setSent(true);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        toast.error(
          error?.response?.data?.message ||
            "Too many attempts. Please wait a few minutes and try again.",
        );
        return;
      }
      toast.error(
        error?.response?.data?.message ||
          "Could not send the reset email. Please try again or contact the college office.",
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900">
      <div className="flex items-center justify-between bg-gray-800 py-2 px-4">
        <div className="flex flex-col md:flex-row items-center justify-between w-full md:w-[90%] mx-auto">
          <img
            src="/agnel-logo.png"
            alt="Agnel School of Law"
            className="h-12 md:h-16 mb-2 md:mb-0"
          />
          <div className="text-center md:text-right text-white text-xs md:text-sm">
            <span className="font-bold block md:inline">AGNEL SCHOOL OF LAW</span>
            <span className="block md:inline md:ml-1">
              Agnel Technical Education Complex, Sector-9A, Vashi, Navi Mumbai
              Maharashtra 400703.
            </span>
          </div>
        </div>
      </div>

      {/* `auth` is required alongside `login-bg`: the stylesheet rule is `.auth.login-bg`,
          so login-bg alone renders a flat background instead of the campus photo. */}
      <div
        className="flex-1 flex items-center justify-center px-4 py-10 auth login-bg"
        style={{ position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            zIndex: 1,
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            maxWidth: "460px",
            backgroundColor: "rgba(30, 58, 138, 0.35)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 18px 45px rgba(0, 0, 0, 0.45)",
          }}
        >
          {sent ? (
            <div className="text-white text-center">
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  margin: "0 auto 1rem",
                  borderRadius: "50%",
                  backgroundColor: "rgba(34, 197, 94, 0.18)",
                  border: "1px solid rgba(34, 197, 94, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="mdi mdi-email-check-outline"
                  style={{ fontSize: "1.75rem", color: "#4ade80" }}
                />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-2">Check your email</h3>
              <p className="text-sm text-gray-200 mb-4">
                If an account matches that address, a new password is on its way.
                It can take a few minutes to arrive — please check your spam
                folder too.
              </p>
              <p className="text-sm text-gray-300 mb-5">
                Still nothing? Contact the college office to have your account
                checked.
              </p>
              <NavLink
                to={config.loginPath}
                className="btn btn-primary w-full"
                style={{
                  background: "rgba(59, 130, 246, 0.9)",
                  border: "none",
                  padding: "0.7rem",
                  display: "block",
                }}
              >
                {config.loginLabel}
              </NavLink>
            </div>
          ) : (
            <>
              <h3 className="text-white text-xl md:text-2xl font-bold mb-1">
                {config.title}
              </h3>
              <p className="text-sm text-gray-300 mb-4">{config.subtitle}</p>

              <div
                style={{
                  backgroundColor: "rgba(59, 130, 246, 0.12)",
                  border: "1px solid rgba(147, 197, 253, 0.35)",
                  borderRadius: "8px",
                  padding: "0.75rem 1rem",
                  marginBottom: "1.25rem",
                }}
              >
                <p className="text-xs md:text-sm text-blue-100 mb-0">
                  We will email you a new password. Log in with it, then change it
                  from your profile.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* The label sits OUTSIDE .form-group deliberately: the global stylesheet has
                    `.form-group label { color: #545050 !important }`, which would render it
                    near-invisible on this dark card and cannot be overridden inline.
                    The input keeps .form-control so it matches the login screens exactly —
                    that rule set is also !important, so styling it here would not win. */}
                <label
                  htmlFor="forgot-email"
                  className="text-white text-sm mb-1 d-block"
                >
                  Email address
                </label>
                <div className="form-group mb-3">
                  <input
                    id="forgot-email"
                    className="form-control p_input w-full"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                    aria-invalid={errors.email ? "true" : "false"}
                    aria-describedby={errors.email ? "forgot-email-error" : undefined}
                    {...register("email", {
                      required: "Email address is required",
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: "Enter a valid email address",
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <p
                    id="forgot-email-error"
                    className="text-red-400 mb-3 text-sm"
                    role="alert"
                  >
                    {errors.email.message}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting
                      ? "rgba(59, 130, 246, 0.5)"
                      : "rgba(59, 130, 246, 0.9)",
                    border: "none",
                    padding: "0.75rem",
                    borderRadius: "6px",
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send new password"}
                </button>

                <div className="text-center mt-4">
                  <NavLink
                    to={config.loginPath}
                    className="text-blue-300 text-sm"
                  >
                    {config.loginLabel}
                  </NavLink>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
